import React, { useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import moment from "moment";
import "./bancamiga.css";

const MOTIVOS = [
  { value: "CE", label: "Transferencia externa (CE)" },
  { value: "CI", label: "Transferencia interna (CI)" },
  { value: "CD", label: "Depósito (CD)" },
];

function hoyISO() {
  const fechaSistema = JSON.parse(localStorage.getItem("fechasistema"));
  return moment.utc(fechaSistema).format("YYYY-MM-DD");
}

function esPlano(v) {
  return v === null || ["string", "number", "boolean"].includes(typeof v);
}

/** Muestra la respuesta de Bancamiga: tabla para campos planos, JSON para anidados. */
function ResultadoPago({ data }) {
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <p className="muted">El banco devolvió una lista vacía.</p>;
    }
    // Array de objetos -> tabla genérica con columnas de la primera fila.
    if (data.every((f) => f && typeof f === "object" && !Array.isArray(f))) {
      const cols = Object.keys(data[0]);
      return (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((fila, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c}>
                      {esPlano(fila[c])
                        ? String(fila[c] ?? "")
                        : JSON.stringify(fila[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
  }

  if (data && typeof data === "object") {
    const filas = Object.entries(data);
    return (
      <div className="result-grid">
        {filas.map(([k, v]) => (
          <div className="result-row" key={k}>
            <span className="result-key">{k}</span>
            <span className="result-value">
              {esPlano(v) ? String(v ?? "") : JSON.stringify(v)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return <pre className="result-pre">{JSON.stringify(data, null, 2)}</pre>;
}

export const ModalBancamiga = ({ show, api, onHide }) => {
  const btnCancela = useRef();

  const [referencia, setReferencia] = useState("");
  const [fecha, setFecha] = useState(hoyISO());
  const [motivo, setMotivo] = useState("CE");
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);

  const blanquear = () => {
    setReferencia("");
    setFecha(hoyISO());
    setMotivo("CE");
    setResultado(null);
    setError("");
    setCargando(false);
  };

  const digitosEsperados = motivo === "CE" ? 8 : 10;

  const consultar = async (e) => {
    e.preventDefault();
    setError("");
    setResultado(null);

    if (!api.cliente_id) {
      return;
    }

    if (
      !referencia ||
      referencia.length !== digitosEsperados ||
      !/^\d+$/.test(referencia)
    ) {
      setError(
        `La referencia debe tener ${digitosEsperados} dígitos para el motivo ${motivo}`,
      );
      return;
    }

    setCargando(true);

    try {
      const response = await axios.post(
        api.host,
        {
          cliente_id: Number(api.cliente_id),
          referencia,
          fecha,
          motivo,
        },
        {
          headers: {
            Authorization: `Bearer ${api.key}`,
          },
        },
      );

      if (response.status === 200 && response.data.codigo === 200) {
        setResultado(response.data.data);
      } else {
        setResultado({ movimientos: [] });
      }
    } catch (err) {
      setResultado({ movimientos: [] });
    } finally {
      setCargando(false);
    }
  };

  const cancelar = () => {
    blanquear();
    onHide();
  };

  return (
    <Modal
      show={show}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      keyboard={false}
      onShow={() => {
        btnCancela.current.focus();
      }}
    >
      <Modal.Header className="azulGradiant text-light">
        <Modal.Title style={{ color: "#fff" }}>
          <i>Consultar Pago</i>
        </Modal.Title>
        <button
          ref={btnCancela}
          className="btn btn-outline-secondary text-white fw-bold"
          onClick={cancelar}
        >
          Salir
        </button>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={consultar}>
          <div className="input-group w-100 mb-3">
            <label className="input-group-text" htmlFor="inputGroupSelect01">
              MOTIVO *
            </label>
            <select
              className="form-select"
              id="inputGroupSelect01"
              value={motivo}
              onChange={(e) => {
                setReferencia("");
                setFecha(hoyISO());
                setMotivo(e.target.value)}}
            >
              {MOTIVOS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group w-100 mb-3">
            <span className="input-group-text" id="inputGroup-sizing1">
              REFERENCIA *
            </span>
            <input
              type="text"
              className="form-control"
              aria-label="Sizing example input"
              aria-describedby="inputGroup-sizing1"
              inputMode="numeric"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value.replace(/\D/g, ""))}
              placeholder={`${digitosEsperados} dígitos`}
              maxLength={10}
              required
            />
          </div>

          <div className="input-group w-100 mb-3">
            <span className="input-group-text" id="inputGroup-sizing2">
              FECHA *
            </span>
            <input
              type="date"
              className="form-control"
              aria-label="Sizing example input"
              aria-describedby="inputGroup-sizing2"
              value={fecha}
              max={hoyISO()}
              onChange={(e) => setFecha(e.target.value)}
              required
            />
          </div>

          <div className="d-flex justify-content-center mb-3">
            Longitud esperada para {motivo}: {digitosEsperados} dígitos
          </div>

          <div className="d-flex justify-content-between mb-3">
            <button
              type="submit"
              className="btn btn-success"
              disabled={cargando}
            >
              {cargando ? (
                <i className="fas fa-sync"> Consultando…</i>
              ) : (
                <i className="fas fa-search"> Consultar pago</i>
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={cargando}
              onClick={blanquear}
            >
              <i className="fas fa-times"> Cancelar</i>
            </button>
          </div>
        </form>

        {error && <div className="alert alert-error">{error}</div>}

        {resultado !== null && (
          <div className="result-card">
            <h3>Respuesta del Banco</h3>
            <ResultadoPago data={resultado} />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
